import { Button, ButtonDropdown } from '@cimplico/components';
import { useEffect, useRef, useState } from 'react';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import { toastNotification } from '../../toast/Toast';
import Editor, { EditorRef } from '../../rich-text-editor/Editor';

type NewReplyProps = {
    defaultRawReply?: string;
    defaultStructuredReply?: string;
    onCancel?: () => void;
    onReply: (rawReply: string, structuredReply: string, setResolve: boolean) => void;
};

function NewReply({ defaultRawReply, defaultStructuredReply, onCancel, onReply }: NewReplyProps): JSX.Element {
    const [reply, setReply] = useState<{ rawReply?: string; structuredReply?: string; errorMessage?: string }>({
        rawReply: defaultRawReply,
        structuredReply: defaultStructuredReply,
    });

    const editorRef = useRef<EditorRef | null>(null);

    const { readOnly } = useWorkpaperContext();

    useEffect(() => {
        setReply({ rawReply: defaultRawReply, structuredReply: defaultStructuredReply, errorMessage: '' });
    }, [defaultRawReply, defaultStructuredReply]);

    function handleSubmit(setResolve: boolean): void {
        if (!reply.rawReply || !reply.structuredReply) {
            setReply({ rawReply: reply.rawReply, structuredReply: reply.structuredReply, errorMessage: 'A reply is required.' });
            toastNotification('error', `Unable to ${defaultRawReply ? 'update' : 'create'} reply!`);
            return;
        }
        onReply(reply.rawReply, reply.structuredReply, setResolve);
        setReply({});
        editorRef.current?.clear();
    }

    function handleReplyChange(rawContent: string, structuredContent: string): void {
        setReply({ rawReply: rawContent, structuredReply: structuredContent, errorMessage: '' });
    }

    return (
        <div className="flex flex-col gap-4 mt-2 pb-8 h-[250px]">
            <div className="h-full">
                <Editor
                    isReplyEditor
                    placeholder="Enter a reply..."
                    defaultContent={defaultStructuredReply}
                    onContentChange={handleReplyChange}
                    errorMessage={reply.errorMessage}
                    ref={editorRef}
                />
            </div>
            <div className="ml-auto mr-0">
                {onCancel && <Button type="hollow" label="Cancel" size="xs" onClick={onCancel} />}
                <ButtonDropdown variant="primary" label="Save" onClick={(): void => handleSubmit(false)} disabled={readOnly} size="xs">
                    <ButtonDropdown.SecondaryAction label="Save and Resolve" onClick={(): void => handleSubmit(true)} />
                </ButtonDropdown>
            </div>
        </div>
    );
}

export default NewReply;
